import React, { ReactNode, createContext, useMemo, useContext, useCallback } from "react";

import { CircularProgress } from "@material-ui/core";

import useAuthSWR from "../hooks/useAuthSWR";
import { FeatureToggleContextProps, FeatureToggleKey } from "../types/featureToggle";
import { FetchedData } from "../types/fetch";
import { OrgMeResponse } from "../types/org";

import { useAuth } from "./auth";

interface ProviderProps {
  children: ReactNode;
}

const FeatureToggleContext = createContext<Partial<FeatureToggleContextProps>>({});

const FeatureToggleProvider = ({ children }: ProviderProps): JSX.Element => {
  const { isAuthenticated } = useAuth();

  const { data, error, isValidating, state } = useAuthSWR<FetchedData<OrgMeResponse>>(
    isAuthenticated ? `/organizations/me` : null,
    {
      revalidateOnFocus: false,
    }
  );

  const featureToggles = useMemo(() => {
    if (!data || !data.data) return {};
    const features = data?.data.features;
    const toggles = features.reduce((acc, feature) => {
      acc[feature.name] = feature.enabled;
      return acc;
    }, {} as Record<string, boolean>);

    return toggles;
  }, [data]);

  const isFeatureEnabled = useCallback(
    (featureName: FeatureToggleKey): boolean =>
      // Fail-safe: default to false for unknown features
      featureToggles[featureName] === true,
    [featureToggles]
  );

  const isLoading = state === "loading" || isValidating;

  const value = useMemo<FeatureToggleContextProps>(
    () => ({
      isFeatureEnabled,
      featureToggles,
      isLoading,
      error,
    }),
    [isFeatureEnabled, featureToggles, isLoading, error]
  );

  if (isLoading && isAuthenticated) {
    return <CircularProgress />;
  }

  return <FeatureToggleContext.Provider value={value}>{children}</FeatureToggleContext.Provider>;
};

const useFeatureToggle = (): FeatureToggleContextProps => {
  const context = useContext(FeatureToggleContext);

  if (!context) {
    throw new Error("useFeatureToggle must be used within a FeatureToggleProvider");
  }

  return context as FeatureToggleContextProps;
};

export { FeatureToggleProvider, useFeatureToggle };
